import { supabase } from '../supabaseClient';

export const cadreConferenceService = {
  async getCadresData(editionYear) {
    try {
      console.log('Getting cadres data for edition:', editionYear);
      
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        console.log(`Edition ${editionYear} not found`);
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get cadres ordered by display_order
      const { data: cadres, error } = await supabase
        .from('cadre_conference')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: true });

      if (error) throw error;

      return cadres || [];
    } catch (error) {
      console.error('Error getting cadres data:', error);
      throw error;
    }
  },

  async addCadre(cadreData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get the highest display_order to add the new cadre at the end
      const { data: maxOrderData } = await supabase
        .from('cadre_conference')
        .select('display_order')
        .eq('edition_id', edition.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const nextOrder = maxOrderData && maxOrderData.length > 0 
        ? (maxOrderData[0].display_order || 0) + 1 
        : 1;

      const insertData = {
        edition_id: edition.id,
        title: cadreData.title,
        display_order: nextOrder
      };

      const { data, error } = await supabase
        .from('cadre_conference')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding cadre:', error);
      throw error;
    }
  },

  async updateCadre(cadreId, cadreData) {
    try {
      const updateData = {
        title: cadreData.title,
        display_order: cadreData.display_order,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('cadre_conference')
        .update(updateData)
        .eq('id', cadreId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating cadre:', error);
      throw error;
    }
  },

  async updateCadreOrder(cadreId, newOrder) {
    try {
      const { data, error } = await supabase
        .from('cadre_conference')
        .update({ display_order: newOrder })
        .eq('id', cadreId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating cadre order:', error);
      throw error;
    }
  },

  async reorderCadres(cadreOrders, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Update all cadres with their new order
      const updates = cadreOrders.map(({ id, order }) => 
        supabase
          .from('cadre_conference')
          .update({ display_order: order })
          .eq('id', id)
          .eq('edition_id', edition.id)
      );

      await Promise.all(updates);
      return true;
    } catch (error) {
      console.error('Error reordering cadres:', error);
      throw error;
    }
  },

  async deleteCadre(cadreId) {
    try {
      const { error } = await supabase
        .from('cadre_conference')
        .delete()
        .eq('id', cadreId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting cadre:', error);
      throw error;
    }
  },

  async getSpeakersInCadre(cadreId, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get speakers in this cadre with their ordering
      const { data: speakerCadres, error } = await supabase
        .from('speaker_cadres')
        .select(`
          display_order,
          speakers!speaker_id (
            id, name, title, organization, type,
            image_url, country, flag_url
          )
        `)
        .eq('cadre_id', cadreId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      return (speakerCadres || []).map(sc => ({
        ...sc.speakers,
        speakerOrderInCadre: sc.display_order
      }));
    } catch (error) {
      console.error('Error getting speakers in cadre:', error);
      throw error;
    }
  },

  async updateSpeakerOrdersInCadre(cadreId, speakerOrders) {
    try {
      // speakerOrders is an array of { speakerId, order }
      const updates = speakerOrders.map(({ speakerId, order }) => 
        supabase
          .from('speaker_cadres')
          .update({ display_order: order })
          .eq('cadre_id', cadreId)
          .eq('speaker_id', speakerId)
      );

      await Promise.all(updates);
      return true;
    } catch (error) {
      console.error('Error updating speaker orders in cadre:', error);
      throw error;
    }
  }
};
